import { useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { ProxiedSignInPage } from '@backstage/core-components';
import type { IdentityApi } from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/plugin-app-react';
import {
  CAPABILITIES_BY_PERSONA,
  DEV_PERSONA_HEADER,
  DEV_PERSONA_PROVIDER_ID,
  Persona,
  isPersona,
} from '@internal/datasphere-personas';
import type { PersonaValue } from '@internal/datasphere-personas';

export const DEV_PERSONA_STORAGE_KEY = 'datasphere.devPersona';

const readStoredPersona = (): PersonaValue | undefined => {
  const stored = window.localStorage.getItem(DEV_PERSONA_STORAGE_KEY);
  return isPersona(stored) ? stored : undefined;
};

// Signing out forgets the persona so the picker shows again.
const forgetPersonaOnSignOut = (identity: IdentityApi): IdentityApi => ({
  getProfileInfo: () => identity.getProfileInfo(),
  getBackstageIdentity: () => identity.getBackstageIdentity(),
  getCredentials: () => identity.getCredentials(),
  signOut: async () => {
    window.localStorage.removeItem(DEV_PERSONA_STORAGE_KEY);
    await identity.signOut();
  },
});

const PersonaPicker = ({
  onPick,
  error,
}: {
  onPick: (persona: PersonaValue) => void;
  error?: Error;
}) => (
  <Box p={4}>
    <Typography variant="h4" gutterBottom>
      Local development sign-in
    </Typography>
    <Typography variant="body1" paragraph>
      SSO is off. Pick the persona to sign in as. Sign out from Settings to
      switch.
    </Typography>
    {error && (
      <Typography color="error" paragraph role="alert">
        Sign-in failed: {error.message}
      </Typography>
    )}
    <Grid container spacing={2}>
      {Object.values(Persona).map(persona => (
        <Grid item xs={12} sm={6} md={3} key={persona}>
          <Card>
            <CardContent>
              <Typography variant="h6">{persona}</Typography>
              <Typography variant="body2" color="textSecondary">
                {CAPABILITIES_BY_PERSONA[persona].join(', ')}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                color="primary"
                variant="contained"
                onClick={() => onPick(persona)}
              >
                Sign in as {persona}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  </Box>
);

type DevPersonaSignInPageProps = SignInPageProps & {
  reload?: () => void;
};

export const DevPersonaSignInPage = ({
  reload = () => window.location.reload(),
  ...props
}: DevPersonaSignInPageProps) => {
  const [persona, setPersona] = useState(readStoredPersona);

  const pick = (chosen: PersonaValue) => {
    window.localStorage.setItem(DEV_PERSONA_STORAGE_KEY, chosen);
    setPersona(chosen);
  };

  if (!persona) {
    return <PersonaPicker onPick={pick} />;
  }

  return (
    <ProxiedSignInPage
      {...props}
      provider={DEV_PERSONA_PROVIDER_ID}
      headers={{ [DEV_PERSONA_HEADER]: persona }}
      onSignInSuccess={identity =>
        props.onSignInSuccess(forgetPersonaOnSignOut(identity))
      }
      ErrorComponent={({ error }) => (
        <PersonaPicker
          error={error}
          onPick={chosen => {
            pick(chosen);
            reload();
          }}
        />
      )}
    />
  );
};
